"use client";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Input,
  Flex,
  Box,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";

const MIN = 512;
const MAX = 1536;

export const CustomRatioModal = ({ isOpen, onClose, onSave }: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (width: number, height: number) => void;
}) => {
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const toast = useToast();

  const handleSave = () => {
    if (width < MIN || width > MAX || height < MIN || height > MAX) {
      toast({
        title: "Invalid dimensions",
        description: `Width and height must be between ${MIN}px and ${MAX}px.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    onSave(width, height);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent
            bg="#0D1117"
            color="white"
            borderRadius="lg"
            w={{ base: "90%", sm: "80%", md: "400px" }} // 👈 Responsive width
            maxW="100%" // Prevents overflow on small screens
            boxShadow="0 0 0 2px rgba(255, 255, 255, 0.15), 0 0 16px rgba(255, 255, 255, 0.1)"
            >
        <ModalHeader fontWeight="bold">Custom Ratio</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={6}>
            {/* Width */}
            <Box>
              <Text mb={2}>Width</Text>
              <Flex align="center" gap={3}>
                <Slider
                  min={MIN}
                  max={MAX}
                  step={16}
                  value={width}
                  onChange={(val) => setWidth(val)}
                >
                  <SliderTrack bg="gray.600">
                    <SliderFilledTrack bg="whiteAlpha.800" />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
                <Input
                  value={width}
                  onChange={(e) => setWidth(Number(e.target.value))}
                  width="85px"
                  type="number"
                  min={MIN}
                  max={MAX}
                  bg="#161B22"
                  border="1px solid #2C313A"
                  color="white"
                />
                <Text color="gray.400">px</Text>
              </Flex>
            </Box>

            {/* Height */}
            <Box>
              <Text mb={2}>Height</Text>
              <Flex align="center" gap={3}>
                <Slider
                  min={MIN}
                  max={MAX}
                  step={16}
                  value={height}
                  onChange={(val) => setHeight(val)}
                >
                  <SliderTrack bg="gray.600">
                    <SliderFilledTrack bg="whiteAlpha.800" />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
                <Input
                  value={height}
                  onChange={(e) => setHeight(Number(e.target.value))}
                  width="85px"
                  type="number"
                  min={MIN}
                  max={MAX}
                  bg="#161B22"
                  border="1px solid #2C313A"
                  color="white"
                />
                <Text color="gray.400">px</Text>
              </Flex>
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter px={3} pt={6}>
            <Flex w="100%" gap={2}>
                <Button
                w="50%"
                bg="transparent"
                border="1px solid #2C313A"
                color="white"
                borderRadius="8px"
                _hover={{ bg: "whiteAlpha.100" }}
                onClick={onClose}
                >
                Cancel
                </Button>
                <Button
                w="50%"
                bgGradient="linear(to-r, #B678FF, #5F9CFF)"
                color="black"
                borderRadius="8px"
                _hover={{ opacity: 0.9 }}
                onClick={handleSave}
                >
                Save
                </Button>
            </Flex>
            </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CustomRatioModal;
